import { Box, Image, Stack, Text } from '@mantine/core'

import {
  MOBILE_BANNER_FONT_FAMILY,
  MOBILE_BANNER_SUBTITLE_FONT_SIZE,
  MOBILE_BANNER_SUBTITLE_LETTER_SPACING,
  MOBILE_BANNER_TITLE_FONT_SIZE,
  MOBILE_BANNER_TITLE_LETTER_SPACING,
} from './brandingMobileLayout'

interface BrandBannerPreviewProps {
  brandTitle: string
  brandSubtitle: string
  imagePreviewUrl: string | null
}

const SKELETON_BUBBLES: Array<{ side: 'left' | 'right'; width: string; height: number }> = [
  { side: 'left', width: '65%', height: 44 },
  { side: 'right', width: '50%', height: 22 },
  { side: 'left', width: '75%', height: 56 },
  { side: 'right', width: '40%', height: 22 },
  { side: 'left', width: '55%', height: 36 },
  { side: 'right', width: '60%', height: 30 },
  { side: 'left', width: '45%', height: 44 },
]

/**
 * Renders the mobile banner preview at ~250px wide (iPhone SE's 247px pill),
 * using real mobile font + avatar sizes for visual parity.
 */
export function BrandBannerPreview({ brandTitle, brandSubtitle, imagePreviewUrl }: BrandBannerPreviewProps) {
  const hasImage = !!imagePreviewUrl
  return (
    <Box
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 24,
        aspectRatio: '9 / 16',
        maxHeight: 480,
        width: '100%',
        maxWidth: 270,
        backgroundColor: 'var(--mantine-color-gray-0)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        overflow: 'hidden',
        position: 'relative',
      }}>
      {/* Status bar spacer */}
      <Box style={{ height: 24, flexShrink: 0 }} />

      <Box
        style={{
          borderRadius: 999,
          padding: '8px',
          margin: '0 10px 8px',
          minHeight: 40,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          justifyContent: hasImage ? 'flex-start' : 'center',
          backgroundColor: 'var(--mantine-color-body)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          zIndex: 1,
        }}>
        {hasImage && (
          <Box
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              overflow: 'hidden',
              flexShrink: 0,
            }}>
            <Image src={imagePreviewUrl} alt="Banner preview" w={56} h={56} fit="cover" />
          </Box>
        )}
        <Stack gap={1} style={{ flex: 1, minWidth: 0 }}>
          <Text
            truncate
            style={{
              fontSize: MOBILE_BANNER_TITLE_FONT_SIZE,
              fontFamily: MOBILE_BANNER_FONT_FAMILY,
              fontWeight: 600,
              letterSpacing: MOBILE_BANNER_TITLE_LETTER_SPACING,
              textTransform: 'uppercase',
              lineHeight: '18px',
              textAlign: hasImage ? 'left' : 'center',
            }}>
            {brandTitle || 'Channel Name'}
          </Text>
          {brandSubtitle ? (
            <Text
              truncate
              style={{
                fontSize: MOBILE_BANNER_SUBTITLE_FONT_SIZE,
                fontFamily: MOBILE_BANNER_FONT_FAMILY,
                fontWeight: 400,
                letterSpacing: MOBILE_BANNER_SUBTITLE_LETTER_SPACING,
                textTransform: 'uppercase',
                lineHeight: '15px',
                opacity: 0.75,
                textAlign: hasImage ? 'left' : 'center',
              }}>
              {brandSubtitle}
            </Text>
          ) : null}
        </Stack>
      </Box>

      {/* Skeleton chat bubbles */}
      <Box style={{ flex: 1, padding: '0 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {SKELETON_BUBBLES.map((bubble, i) => (
          <Box
            key={i}
            style={{
              alignSelf: bubble.side === 'left' ? 'flex-start' : 'flex-end',
              width: bubble.width,
              height: bubble.height,
              borderRadius: 12,
              backgroundColor: bubble.side === 'left' ? 'var(--mantine-color-gray-2)' : 'var(--mantine-color-gray-3)',
            }}
          />
        ))}
      </Box>

      {/* Bottom nav island */}
      <Box
        style={{
          position: 'absolute',
          bottom: 14,
          right: 14,
          display: 'flex',
          gap: 8,
          backgroundColor: 'var(--mantine-color-body)',
          borderRadius: 999,
          padding: '10px 14px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
        }}>
        <Box style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: 'var(--mantine-color-gray-3)' }} />
        <Box style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: 'var(--mantine-color-gray-4)' }} />
        <Box style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: 'var(--mantine-color-gray-3)' }} />
      </Box>
    </Box>
  )
}
